import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
    collection,
    getDocs,
    deleteDoc,
    doc,
} from "firebase/firestore";
import {
    Store,
    Plus,
    Edit,
    Trash2,
    MapPin,
    Phone,
    Clock,
    Search,
    X,
    Loader,
} from "lucide-react";

const StoreManager = () => {
    const navigate = useNavigate();
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        fetchStores();
    }, []);

    const fetchStores = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "stores"));
            const storesData = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setStores(storesData);
        } catch (error) {
            console.error("Error fetching stores:", error);
            alert("Error al cargar las sucursales");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateStore = () => {
        navigate("/admin/store/new");
    };

    const handleEditStore = (storeId) => {
        navigate(`/admin/store/edit/${storeId}`);
    };

    const handleDelete = async (storeId, storeName) => {
        if (
            window.confirm(
                `¿Estás seguro de que deseas eliminar la sucursal "${storeName}"?`
            )
        ) {
            try {
                await deleteDoc(doc(db, "stores", storeId));
                alert("Sucursal eliminada exitosamente");
                fetchStores();
            } catch (error) {
                console.error("Error deleting store:", error);
                alert("Error al eliminar la sucursal");
            }
        }
    };

    const filteredStores = stores.filter((store) =>
        store.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        store.address.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-[#FF0000] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando sucursales...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Gestión de Sucursales</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {stores.length} sucursal{stores.length !== 1 ? "es" : ""} registrada{stores.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <button
                    onClick={handleCreateStore}
                    className="bg-[#FF0000] text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-[#CC0000] transition-all flex items-center gap-2 shadow-md"
                >
                    <Plus className="w-5 h-5" strokeWidth={2} />
                    Nueva Sucursal
                </button>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-gray-400" strokeWidth={2} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre o dirección..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent text-gray-800 text-sm font-medium placeholder-gray-400 focus:outline-none"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-gray-600" />
                        </button>
                    )}
                </div>
            </div>

            {/* Stores Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStores.length > 0 ? (
                    filteredStores.map((store) => (
                        <div
                            key={store.id}
                            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                        >
                            {/* Store Image */}
                            {store.photo ? (
                                <img
                                    src={store.photo}
                                    alt={store.storeName}
                                    className="w-full h-40 object-cover"
                                />
                            ) : (
                                <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                    <Store className="w-12 h-12 text-gray-400" strokeWidth={1.5} />
                                </div>
                            )}

                            {/* Store Content */}
                            <div className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                    <h3 className="text-lg font-bold text-gray-900 line-clamp-1">
                                        {store.storeName}
                                    </h3>
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs font-semibold ${store.isActive
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {store.isActive ? "Activa" : "Inactiva"}
                                    </span>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-start gap-2">
                                        <MapPin className="w-4 h-4 text-[#FF0000] mt-0.5 flex-shrink-0" strokeWidth={2} />
                                        <p className="text-xs text-gray-600 line-clamp-2">{store.address}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-[#FF0000] flex-shrink-0" strokeWidth={2} />
                                        <p className="text-xs text-gray-600">{store.phoneNumber}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-[#FF0000] flex-shrink-0" strokeWidth={2} />
                                        <p className="text-xs text-gray-600">
                                            {store.openingTime} - {store.closingTime}
                                        </p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEditStore(store.id)}
                                        className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Edit className="w-4 h-4" strokeWidth={2} />
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDelete(store.id, store.storeName)}
                                        className="bg-red-50 text-red-600 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-red-100 transition-colors flex items-center justify-center"
                                    >
                                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-span-full text-center py-12 bg-white rounded-2xl border border-gray-100">
                        <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
                        <p className="text-gray-500">
                            {searchQuery
                                ? "No se encontraron sucursales"
                                : "No hay sucursales registradas"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StoreManager;

